import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Upload, Loader2, Eye, PenLine, Bold, Italic, Heading2, Heading3, List, Quote, Link2, ImagePlus, ExternalLink, Youtube, Images } from "lucide-react";
import { api, fileUrl, errorMessage, uploadImage, youtubeId } from "@/lib/api";
import { Pagination, usePaged } from "@/components/Pagination";
import { useAuth } from "@/lib/auth";
import { Markdown } from "@/lib/markdown";

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", author: "", tags: "", published: true };

export default function AdminBlog() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, refetch, isLoading } = useQuery({ queryKey: ["adm-blog"], queryFn: () => api.get("/admin/blog").then((r) => r.data), enabled: user?.role === "super_admin" });
  const [editing, setEditing] = React.useState(null);
  if (user?.role !== "super_admin") return <div className="bg-white border p-8 text-sm text-neutral-600">Only the Super Admin can manage blog posts.</div>;
  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["blog"] }); };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try { await api.delete(`/admin/blog/${p.id}`); toast.success("Post deleted"); refresh(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const togglePublish = async (p) => {
    try { await api.patch(`/admin/blog/${p.id}`, { ...p, published: !p.published }); toast.success(p.published ? "Moved to drafts" : "Published"); refresh(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  if (editing) return <PostEditor initial={editing === "new" ? EMPTY : { ...EMPTY, ...editing, tags: (editing.tags || []).join(", ") }} id={editing === "new" ? null : editing.id}
                                  onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }}/>;

  const posts = data || [];
  return <BlogRows posts={posts} isLoading={isLoading} setEditing={setEditing} remove={remove} togglePublish={togglePublish}/>;
}

function BlogRows({ posts, isLoading, setEditing, remove, togglePublish }) {
  const pg = usePaged(posts, 10);
  return (
    <div>
      <div className="flex justify-between items-end gap-3 flex-wrap mb-6">
        <div>
          <h1 className="h2-editorial">Blog</h1>
          <p className="text-sm text-neutral-500 mt-1">{posts.filter((p) => p.published).length} published · {posts.filter((p) => !p.published).length} drafts</p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-silk" data-testid="blog-new"><Plus size={12} className="mr-1"/>New post</button>
      </div>
      <div className="bg-white border divide-y">
        {isLoading && <div className="p-6 text-sm text-neutral-500">Loading…</div>}
        {!isLoading && posts.length === 0 && <div className="p-10 text-center text-sm text-neutral-500">No posts yet. Click <b>New post</b> to write the first one.</div>}
        {pg.items.map((p) => (
          <div key={p.id} className="p-4 flex gap-4 items-center flex-wrap sm:flex-nowrap" data-testid="blog-row">
            <div className="w-24 aspect-[4/3] rounded-lg overflow-hidden bg-neutral-100 shrink-0">
              {p.cover_image ? <img src={fileUrl(p.cover_image)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full bg-gradient-to-br from-[#0E4D34] to-[#2D7A5B]"/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold truncate">{p.title}</span>
                {p.published
                  ? <span className="text-[10px] uppercase tracking-widest text-[#0E4D34] bg-[#E8F3EE] px-1.5 py-0.5">Published</span>
                  : <span className="text-[10px] uppercase tracking-widest text-neutral-600 bg-neutral-100 px-1.5 py-0.5">Draft</span>}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5 truncate">{(p.tags || []).join(" · ")}{(p.tags || []).length ? " — " : ""}{new Date(p.updated_at || p.created_at).toLocaleDateString()}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              {p.published && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs border px-3 py-1.5 flex items-center gap-1 hover:border-[#0E4D34]"><ExternalLink size={12}/>View</a>}
              <button onClick={() => togglePublish(p)} className="text-xs border px-3 py-1.5 hover:border-[#0E4D34]">{p.published ? "Unpublish" : "Publish"}</button>
              <button onClick={() => setEditing(p)} className="text-xs border px-3 py-1.5 flex items-center gap-1 hover:border-[#0E4D34]" data-testid="blog-edit"><Edit2 size={12}/>Edit</button>
              <button onClick={() => remove(p)} aria-label="Delete post" className="text-[#991B1B] p-1.5"><Trash2 size={15}/></button>
            </div>
          </div>
        ))}
      </div>
      <Pagination {...pg.props} label="posts"/>
    </div>
  );
}

function PostEditor({ initial, id, onCancel, onSaved }) {
  const [f, setF] = React.useState(initial);
  const [busy, setBusy] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [preview, setPreview] = React.useState(false);
  const ta = React.useRef(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  // insert formatting at the cursor / around the selection
  const wrap = (before, after = before, placeholder = "text") => {
    const el = ta.current; if (!el) return;
    const { selectionStart: a, selectionEnd: b, value } = el;
    const sel = value.slice(a, b) || placeholder;
    const next = value.slice(0, a) + before + sel + after + value.slice(b);
    setF((x) => ({ ...x, content: next }));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + before.length, a + before.length + sel.length); });
  };
  const linePrefix = (prefix) => {
    const el = ta.current; if (!el) return;
    const { selectionStart: a, selectionEnd: b, value } = el;
    const start = value.lastIndexOf("\n", a - 1) + 1;
    const block = value.slice(start, b) || "text";
    const replaced = block.split("\n").map((l) => prefix + l.replace(/^(#{1,3}\s|[-*]\s|>\s?)/, "")).join("\n");
    const next = value.slice(0, start) + replaced + value.slice(b);
    setF((x) => ({ ...x, content: next }));
    requestAnimationFrame(() => el.focus());
  };
  const insertAtCursor = (text) => {
    const el = ta.current; const value = f.content || "";
    const pos = el ? el.selectionStart : value.length;
    const before = value.slice(0, pos); const after = value.slice(pos);
    const pad = before && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : "";
    setF((x) => ({ ...x, content: before + pad + text + "\n\n" + after }));
  };
  const addLink = () => {
    const url = window.prompt("Link address (https://…)", "https://");
    if (url && url !== "https://") wrap("[", `](${url.trim()})`, "link text");
  };
  // one photo → full width; several photos at once → shown as a photo gallery grid
  const addImage = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 12); e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const lines = [];
      for (const file of files) {
        const url = await uploadImage(file, "blog");
        lines.push(`![${file.name.replace(/\.[^.]+$/, "").replace(/[[\]]/g, "")}](${url})`);
      }
      insertAtCursor(lines.join("\n"));
      toast.success(lines.length > 1 ? `${lines.length} photos added as a gallery` : "Image added to the post");
    }
    catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };
  const addVideo = () => {
    const url = window.prompt("Paste a YouTube link (video or Shorts)", "https://www.youtube.com/watch?v=");
    if (!url) return;
    if (!youtubeId(url)) return toast.error("That doesn't look like a YouTube link");
    insertAtCursor(url.trim());
    toast.success("Video added — it shows as a player in the post");
  };
  const onCover = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file, "blog"); set("cover_image")(url); }
    catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };
  const save = async (publish) => {
    if (!f.title.trim()) return toast.error("Please enter a title");
    setBusy(true);
    try {
      const payload = { ...f, published: publish ?? f.published, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (id) await api.patch(`/admin/blog/${id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(payload.published ? "Post published" : "Saved as draft");
      onSaved();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const tool = "p-2 rounded hover:bg-neutral-100 text-neutral-700";

  return (
    <div data-testid="blog-editor">
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <h1 className="h2-editorial">{id ? "Edit post" : "New post"}</h1>
        <button onClick={onCancel} className="text-sm text-neutral-500 hover:text-neutral-900 flex items-center gap-1"><X size={16}/>Close</button>
      </div>
      <div className="grid xl:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="bg-white border p-5 sm:p-6 space-y-4">
          <input value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="Post title" data-testid="blog-title"
                 className="w-full text-2xl sm:text-3xl font-semibold border-0 border-b border-neutral-200 pb-3 focus:outline-none focus:border-[#0E4D34]"/>
          <div className="flex items-center justify-between gap-2 flex-wrap border-b pb-2">
            <div className="flex items-center gap-0.5 flex-wrap">
              <button type="button" title="Heading" onClick={() => linePrefix("## ")} className={tool}><Heading2 size={16}/></button>
              <button type="button" title="Sub-heading" onClick={() => linePrefix("### ")} className={tool}><Heading3 size={16}/></button>
              <button type="button" title="Bold" onClick={() => wrap("**")} className={tool}><Bold size={16}/></button>
              <button type="button" title="Italic" onClick={() => wrap("*")} className={tool}><Italic size={16}/></button>
              <button type="button" title="Bullet list" onClick={() => linePrefix("- ")} className={tool}><List size={16}/></button>
              <button type="button" title="Quote" onClick={() => linePrefix("> ")} className={tool}><Quote size={16}/></button>
              <button type="button" title="Link" onClick={addLink} className={tool}><Link2 size={16}/></button>
              <label title="Insert image(s) — pick several to make a gallery" className={`${tool} cursor-pointer inline-flex items-center gap-1`}>
                {uploading ? <Loader2 size={16} className="animate-spin"/> : <><ImagePlus size={16}/><Images size={14} className="text-neutral-400"/></>}
                <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={addImage} className="hidden" data-testid="blog-insert-images"/>
              </label>
              <button type="button" title="Insert YouTube video" onClick={addVideo} className={tool} data-testid="blog-insert-video"><Youtube size={16}/></button>
            </div>
            <div className="flex rounded-full border overflow-hidden text-xs">
              <button type="button" onClick={() => setPreview(false)} className={`px-3 py-1.5 flex items-center gap-1 ${!preview ? "bg-[#0E4D34] text-white" : ""}`}><PenLine size={12}/>Write</button>
              <button type="button" onClick={() => setPreview(true)} className={`px-3 py-1.5 flex items-center gap-1 ${preview ? "bg-[#0E4D34] text-white" : ""}`} data-testid="blog-preview"><Eye size={12}/>Preview</button>
            </div>
          </div>
          {preview ? (
            <div className="min-h-[420px] py-2"><Markdown text={f.content || "*Nothing written yet.*"}/></div>
          ) : (
            <textarea ref={ta} value={f.content} onChange={(e) => set("content")(e.target.value)} rows={18} data-testid="blog-content"
                      placeholder={"Write your post here.\n\n## A heading\nA paragraph with **bold** and *italic* words.\n\n- A bullet point\n- Another one"}
                      className="w-full min-h-[420px] text-[15px] leading-relaxed focus:outline-none resize-y"/>
          )}
          <p className="text-[11px] text-neutral-400">Tip: leave an empty line between paragraphs. Use the toolbar for headings, bold, lists, links, photos (pick several at once for a gallery) and YouTube videos (a YouTube link on its own line becomes a video player).</p>
        </div>

        <div className="space-y-4 xl:sticky xl:top-6">
          <div className="bg-white border p-5 space-y-3">
            <div className="text-xs uppercase tracking-widest text-neutral-600">Cover image</div>
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-neutral-100 border">
              {f.cover_image ? <img src={fileUrl(f.cover_image)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-xs text-neutral-400">No cover</div>}
            </div>
            <div className="flex gap-2 items-center">
              <label className="btn-outline-silk cursor-pointer flex-1">{uploading ? <Loader2 size={12} className="mr-2 animate-spin"/> : <Upload size={12} className="mr-2"/>}{f.cover_image ? "Replace" : "Upload"}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onCover} className="hidden" data-testid="blog-cover"/></label>
              {f.cover_image && <button type="button" onClick={() => set("cover_image")("")} className="text-xs text-[#991B1B]">Remove</button>}
            </div>
          </div>
          <div className="bg-white border p-5 space-y-3">
            <Field label="Tags (comma separated)" value={f.tags} onChange={set("tags")} placeholder="Saree care, Styling" testid="blog-tags"/>
            <Field label="Author (optional)" value={f.author} onChange={set("author")} placeholder="Avishka Navya"/>
            <div>
              <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">Short summary (optional)</label>
              <textarea value={f.excerpt} onChange={(e) => set("excerpt")(e.target.value)} rows={3} maxLength={400} placeholder="Shown on the blog list. Leave empty to use the first lines of the post."
                        className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
            </div>
          </div>
          <div className="bg-white border p-5 space-y-2">
            <button onClick={() => save(true)} disabled={busy || uploading} className="btn-silk w-full disabled:opacity-50" data-testid="blog-publish">{busy ? "Saving…" : id && initial.published ? "Update & keep published" : "Publish"}</button>
            <button onClick={() => save(false)} disabled={busy || uploading} className="btn-outline-silk w-full disabled:opacity-50" data-testid="blog-draft">Save as draft</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
             className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
    </div>
  );
}
